import React from 'react'
import { NavLink } from 'react-router-dom'
import { Brain, Heart, Star, Settings, LogIn, User, PlayCircle, Award, Bookmark, Check, LifeBuoy, Gift } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import { useUser } from '@/hooks/useUser'
import { useAuth } from '@/contexts/AuthContext'
import { useInitialOnboarding } from '@/hooks/useInitialOnboarding'
import { useAssessment } from '@/features/assessments/hooks/useAssessment'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { routes } from '@/lib/routes'
import { useAIProfile } from '@/features/home/hooks/useAIProfile'
import { useTop3Studies } from '@/hooks/useTop3Studies'

// Helper function to determine if an item should show a checkmark
const useNavItemStatus = (itemName: string): boolean => {
  const { data: initialOnboarding } = useInitialOnboarding()
  const { data: personalityAssessment } = useAssessment('personality')
  const { data: interestsAssessment } = useAssessment('interests')
  const { data: talentsAssessment } = useAssessment('talents')

  if (itemName === 'Start' && initialOnboarding?.done) {
    return true
  } else if (itemName === 'Persoonlijkheid' && personalityAssessment?.completed) {
    return true
  } else if (itemName === 'Interesses' && interestsAssessment?.completed) {
    return true
  } else if (itemName === 'Talenten' && talentsAssessment?.completed) {
    return true
  }

  return false
}

// Shared class builder for nav links
const baseNavItemClasses = [
  'flex h-10 items-center gap-2.5 px-2.5 rounded-lg transition-all duration-200',
  'hover:bg-accent hover:text-accent-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
]

const navItemClasses = (isActive: boolean, isMobile?: boolean) =>
  cn(
    baseNavItemClasses,
    isActive ? 'bg-primary/10 text-primary ring-1 ring-primary/20' : 'text-muted-foreground',
    !isMobile && 'group relative'
  )

// Reusable navigation item component
type NavItem = {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

interface NavigationItemProps {
  item: NavItem
  isMobile?: boolean
  onNavigate?: () => void
  showDot?: boolean
}

const NavigationItem = React.memo(function NavigationItem({ item, isMobile = false, onNavigate, showDot }: NavigationItemProps) {
  const showCheckmark = useNavItemStatus(item.name)
  const Icon = item.icon

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      className={({ isActive }) => navItemClasses(isActive, isMobile)}
      aria-label={`Navigate to ${item.name}${showCheckmark ? ' (completed)' : ''}`}
    >
      <Icon className="h-4 w-4" />
      <span className="text-base font-medium">{item.name}</span>
      {showDot && (
        <span className="ml-auto h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />
      )}
      {showCheckmark && (
        <Check className="h-3 w-3 text-green-600 ml-auto" aria-hidden="true" />
      )}
    </NavLink>
  )
})

const navigationItems: NavItem[] = [
  {
    name: 'Start',
    href: routes.INITIAL_ONBOARDING,
    icon: PlayCircle,
  },
  {
    name: 'Interesses',
    href: routes.ASSESSMENTS.INTERESTS,
    icon: Heart,
  },
  {
    name: 'Talenten',
    href: routes.ASSESSMENTS.TALENTS,
    icon: Star,
  },
   {
    name: 'Persoonlijkheid',
    href: routes.ASSESSMENTS.PERSONALITY,
    icon: Brain,
  },
  {
    name: 'Mijn Profiel',
    href: routes.MY_PROFILE,
    icon: User,
  },
  {
    name: 'Top-3 Studies',
    href: routes.TOP_3_STUDIES,
    icon: Award,
  },
  {
    name: 'Favoriete Studies',
    href: routes.FAVORITES,
    icon: Bookmark,
  },
]

function NavigationList({ isMobile = false, onItemClick }: { isMobile?: boolean; onItemClick?: () => void }) {
  // Compute readiness: all steps complete AND no AI profile yet
  const { data: initialOnboarding } = useInitialOnboarding()
  const { data: personalityAssessment } = useAssessment('personality')
  const { data: interestsAssessment } = useAssessment('interests')
  const { data: talentsAssessment } = useAssessment('talents')
  const { user } = useUser()
  const { data: aiProfileData } = useAIProfile(user?.id)
  const { hasData: hasTop3Studies, generatedAt: top3GeneratedAt } = useTop3Studies(user?.id || null)
  const { top3JustGenerated, setTop3JustGenerated } = useNavigationStore()

  const isFullyCompleted = Boolean(
    initialOnboarding?.done &&
    personalityAssessment?.completed &&
    interestsAssessment?.completed &&
    talentsAssessment?.completed
  )
  const showProfileDot = isFullyCompleted && !aiProfileData?.ai_profile_description
  const hasTop3 = hasTop3Studies || Boolean(top3GeneratedAt)
  const showTop3Dot = Boolean(aiProfileData?.ai_profile_description) && !hasTop3 && !top3JustGenerated

  // If the flag is set but store later reloads with existing data, reset the flag
  // ensures the dot logic stays consistent across sessions
  if (top3JustGenerated && hasTop3) {
    setTop3JustGenerated(false)
  }

  return (
    <div className="flex flex-col gap-1 p-4 flex-1">
      {navigationItems.map((item) => (
        <React.Fragment key={item.name}>
          <NavigationItem 
            item={item} 
            isMobile={isMobile} 
            onNavigate={onItemClick} 
            showDot={(item.name === 'Mijn Profiel' && showProfileDot) || (item.name === 'Top-3 Studies' && showTop3Dot)}
          />
          {item.name === 'Persoonlijkheid' && <Separator className="my-2" />}
        </React.Fragment>
      ))}
    </div>
  )
}

function BottomSection() {
  const { user, isLoading, profile } = useUser()
  const { accountType } = useAuth()
  const { data: initialOnboarding } = useInitialOnboarding()
  const { setNavCollapsed } = useNavigationStore()

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Separator />
        <div className="px-4 space-y-2">
          {/* Preferences skeleton */}
          <div className="flex h-12 items-center gap-3 px-3 rounded-lg">
            <div className="h-5 w-5 bg-muted rounded animate-pulse" />
            <div className="h-4 w-20 bg-muted rounded animate-pulse" />
          </div>
          {/* Profile skeleton */}
          <div className="flex h-12 items-center gap-3 px-3 rounded-lg">
            <div className="h-7 w-7 bg-muted rounded-full animate-pulse" />
            <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="space-y-2">
        <Separator />
        <div className="px-4">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start h-12"
            onClick={() => setNavCollapsed(true)}
            asChild
          >
            <NavLink to={routes.SIGNIN} aria-label="Sign in to your account">
              <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
              Sign in
            </NavLink>
          </Button>
        </div>
      </div>
    )
  }

  const displayName = profile?.first_name ?? initialOnboarding?.answers?.first_name ?? 'Account'

  return (
    <div className="space-y-2">
      <Separator />
      <div className="px-4 space-y-1 pb-4">
        {accountType === 'consumer' && (
          <NavLink
            to={routes.BONUSSEN}
            onClick={() => setNavCollapsed(true)}
            className={({ isActive }) => navItemClasses(isActive)}
            data-testid="nav-bonussen"
            aria-label="Bonussen"
          >
            <Gift className="h-4 w-4" aria-hidden="true" />
            <span className="text-base font-medium">Bonussen</span>
          </NavLink>
        )}

        <NavLink
          to={routes.PREFERENCES}
          onClick={() => setNavCollapsed(true)}
          className={({ isActive }) => navItemClasses(isActive)}
          data-testid="nav-preferences"
          aria-label="Account preferences"
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
          <span className="text-base font-medium">Instellingen</span> 
        </NavLink>

        <NavLink
          to={routes.SUPPORT}
          onClick={() => setNavCollapsed(true)}
          className={({ isActive }) => navItemClasses(isActive)}
          data-testid="nav-support"
          aria-label="Support en contact"
        >
          <LifeBuoy className="h-4 w-4" aria-hidden="true" />
          <span className="text-base font-medium">Support</span>
        </NavLink>

        <NavLink
          to={routes.HOME}
          onClick={() => setNavCollapsed(true)}
          className="flex h-10 items-center gap-2.5 px-2.5 rounded-lg transition-all duration-200 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="nav-mini-profile"
          aria-label={displayName ? `Go to dashboard, logged in as ${displayName}` : 'Go to dashboard'}
        >
          <User className="h-4 w-4" aria-hidden="true" />
          {displayName && (
            <span className="text-base font-medium truncate">{displayName}</span>
          )}
        </NavLink>
      </div>
    </div>
  )
}

export function LeftNavigation() {
  const { isNavCollapsed, setNavCollapsed } = useNavigationStore()

  return (
    <>
      {/* Desktop Navigation */}
      <nav 
        className="hidden lg:flex fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 flex-col border-r bg-background z-30"
        aria-label="Main navigation"
      >
        <NavigationList />
        <BottomSection />
      </nav>

      {/* Mobile Navigation */}
      <nav 
        className={cn(
          'lg:hidden fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 flex-col border-r bg-background z-40 transition-transform duration-300 ease-in-out',
          isNavCollapsed ? '-translate-x-full' : 'translate-x-0'
        )}
        aria-label="Main navigation"
        aria-hidden={isNavCollapsed}
      >
        <NavigationList isMobile onItemClick={() => setNavCollapsed(true)} />
        <BottomSection />
      </nav>
    </>
  )
}